import ExcelJS from 'exceljs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadTurkishFont, TURKISH_FONT } from '../../../shared/utils/pdfTurkishFont';

/** Row types for styling - maps from API lineType */
const ROW_LINE = 'line';
const ROW_TOTAL = 'total';
const ROW_BLOCK = 'block';

/**
 * Builds label from income statement row.
 * Replaces TOTAL with TOPLAM for total/calculated rows.
 * @param {Object} row - { accountCode, accountName, category, lineType }
 * @param {boolean} [replaceTotal] - If true, replace TOTAL with TOPLAM
 * @returns {string}
 */
function buildLabel(row, replaceTotal = false) {
  let parts = [row.accountCode, row.accountName, row.category].filter(Boolean);
  let label = parts.join(' ');
  if (replaceTotal) {
    label = label.replace(/_TOTAL\b/g, '_TOPLAM').replace(/\bTOTAL\b/g, 'TOPLAM');
  }
  return label;
}

/**
 * Builds rows for Excel export with raw numeric values and row type for styling.
 * @param {Array} rows - Income statement rows from API
 * @param {boolean} hideZeroLines - Whether to filter zero detail rows
 * @returns {Array<{label: string, amount: number, type: string}>}
 */
function buildExcelRows(rows, hideZeroLines) {
  const toNum = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);
  const mapType = (lineType) => {
    if (lineType === 'TOTAL') return ROW_TOTAL;
    if (lineType === 'CALCULATED') return ROW_BLOCK;
    return ROW_LINE;
  };

  return rows
    .filter((row) => {
      if (hideZeroLines && row.lineType === 'DETAIL') {
        return Math.abs(toNum(row.amount)) >= 0.01;
      }
      return true;
    })
    .map((row) => ({
      label: buildLabel(row, row.lineType === 'TOTAL' || row.lineType === 'CALCULATED'),
      amount: toNum(row.amount),
      type: mapType(row.lineType)
    }));
}

/**
 * Builds rows for PDF export with formatted amounts and row type for styling.
 * @param {Array} rows - Income statement rows from API
 * @param {boolean} hideZeroLines - Whether to filter zero detail rows
 * @returns {Array<{label: string, amountStr: string, type: string, isAltLine?: boolean}>}
 */
function buildPdfRowsWithType(rows, hideZeroLines) {
  const formatAmount = (v) => {
    const n = typeof v === 'number' && !Number.isNaN(v) ? v : 0;
    return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' TL';
  };
  const toNum = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);
  const mapType = (lineType) => {
    if (lineType === 'TOTAL') return ROW_TOTAL;
    if (lineType === 'CALCULATED') return ROW_BLOCK;
    return ROW_LINE;
  };

  let lineRowCount = 0;
  return rows
    .filter((row) => {
      if (hideZeroLines && row.lineType === 'DETAIL') {
        return Math.abs(toNum(row.amount)) >= 0.01;
      }
      return true;
    })
    .map((row) => {
      const type = mapType(row.lineType);
      const isAltLine = type === ROW_LINE && (lineRowCount++ % 2 === 1);
      return {
        label: buildLabel(row, row.lineType === 'TOTAL' || row.lineType === 'CALCULATED'),
        amountStr: formatAmount(row.amount),
        type,
        isAltLine: type === ROW_LINE ? isAltLine : false
      };
    });
}

/** Modern color palette - same as balance sheet */
const COLORS = {
  headerBg: 'FF2D3748',
  headerFg: 'FFFFFFFF',
  blockBg: 'FF718096',
  blockFg: 'FFFFFFFF',
  lineBg: 'FFFFFFFF',
  lineAltBg: 'FFF7FAFC',
  lineFg: 'FF2D3748',
  totalBg: 'FF3182CE',
  totalFg: 'FFFFFFFF',
  border: 'FFCBD5E0'
};

/**
 * Export income statement to Excel (.xlsx) with modern formatting.
 * Numbers are raw values (no TL suffix), suitable for formulas.
 * @param {Array} rows - Income statement rows from API
 * @param {string} filename - Output filename (without extension)
 * @param {boolean} [hideZeroLines=false] - Filter zero detail rows
 */
export async function exportToExcel(rows, filename = 'gelir_tablosu', hideZeroLines = false) {
  const dataRows = buildExcelRows(rows, hideZeroLines);
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Gelir Tablosu', {
    views: [{ rightToLeft: false }],
    properties: { defaultRowHeight: 20 }
  });

  ws.columns = [{ width: 55 }, { width: 20 }];

  const applyCellStyle = (cell, font, fill, alignment) => {
    if (font) cell.font = font;
    if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
    if (alignment) cell.alignment = alignment;
    cell.border = {
      top: { style: 'thin', color: { argb: COLORS.border } },
      left: { style: 'thin', color: { argb: COLORS.border } },
      bottom: { style: 'thin', color: { argb: COLORS.border } },
      right: { style: 'thin', color: { argb: COLORS.border } }
    };
  };

  const headerRow = ws.addRow(['Hesap / Açıklama', 'Tutar']);
  headerRow.height = 28;
  applyCellStyle(headerRow.getCell(1), { bold: true, size: 11, color: { argb: COLORS.headerFg } }, COLORS.headerBg, { horizontal: 'left', vertical: 'middle' });
  applyCellStyle(headerRow.getCell(2), { bold: true, size: 11, color: { argb: COLORS.headerFg } }, COLORS.headerBg, { horizontal: 'right', vertical: 'middle' });

  let lineIndex = 0;
  dataRows.forEach((r) => {
    const row = ws.addRow([r.label, r.amount]);
    const c1 = row.getCell(1);
    const c2 = row.getCell(2);
    c2.numFmt = '#,##0.00';
    c2.alignment = { horizontal: 'right', vertical: 'middle' };
    c1.alignment = { vertical: 'middle' };

    const isAltLine = r.type === ROW_LINE && (lineIndex++ % 2 === 1);
    let font, fill;

    switch (r.type) {
      case ROW_BLOCK:
        font = { bold: true, size: 10, color: { argb: COLORS.blockFg } };
        fill = COLORS.blockBg;
        break;
      case ROW_LINE:
        font = { size: 9, color: { argb: COLORS.lineFg } };
        fill = isAltLine ? COLORS.lineAltBg : null;
        break;
      case ROW_TOTAL:
        font = { bold: true, size: 10, color: { argb: COLORS.totalFg } };
        fill = COLORS.totalBg;
        break;
      default:
        font = { size: 9, color: { argb: COLORS.lineFg } };
        fill = isAltLine ? COLORS.lineAltBg : null;
    }

    applyCellStyle(c1, font, fill, null);
    applyCellStyle(c2, font, fill, null);
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

/** PDF color palette - RGB arrays for jspdf-autotable */
const PDF_COLORS = {
  headerBg: [45, 55, 72],
  headerFg: [255, 255, 255],
  blockBg: [113, 128, 150],
  blockFg: [255, 255, 255],
  totalBg: [49, 130, 206],
  totalFg: [255, 255, 255],
  lineAltBg: [247, 250, 252],
  lineFg: [45, 55, 72],
  lineBorder: [203, 213, 224]
};

/**
 * Export income statement to PDF with modern styling.
 * Uses DejaVu Sans font for correct Turkish character rendering (ğ, ı, İ, ş, ü, ö, ç).
 * @param {Array} rows - Income statement rows from API
 * @param {string} filename - Output filename (without extension)
 * @param {string} [headerText] - Optional header (e.g. company + period)
 * @param {boolean} [hideZeroLines=false] - Filter zero detail rows
 */
export async function exportToPdf(rows, filename = 'gelir_tablosu', headerText = '', hideZeroLines = false) {
  const dataRows = buildPdfRowsWithType(rows, hideZeroLines);
  const body = dataRows.map((r) => [r.label, r.amountStr]);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  await loadTurkishFont(doc);
  doc.setFont(TURKISH_FONT, 'normal');

  const margin = 14;

  if (headerText) {
    doc.setFontSize(10);
    doc.setTextColor(45, 55, 72);
    doc.text(headerText, margin, 12);
  }

  autoTable(doc, {
    head: [['Hesap / Açıklama', 'Tutar (TL)']],
    body,
    startY: headerText ? 18 : 14,
    margin: { left: margin, right: margin },
    styles: { fontSize: 8, font: TURKISH_FONT, textColor: PDF_COLORS.lineFg, lineWidth: 0.1, lineColor: PDF_COLORS.lineBorder },
    headStyles: {
      font: TURKISH_FONT,
      fillColor: PDF_COLORS.headerBg,
      textColor: PDF_COLORS.headerFg,
      fontStyle: 'bold',
      halign: 'left'
    },
    columnStyles: {
      0: { cellWidth: 130, halign: 'left' },
      1: { cellWidth: 45, halign: 'right' }
    },
    theme: 'plain',
    didParseCell: (data) => {
      if (data.section === 'head') {
        data.cell.styles.fillColor = PDF_COLORS.headerBg;
        data.cell.styles.textColor = PDF_COLORS.headerFg;
        data.cell.styles.fontStyle = 'bold';
        if (data.column.index === 1) data.cell.styles.halign = 'right';
      }
      if (data.section === 'body') {
        const rowData = dataRows[data.row.index];
        const { type, isAltLine } = rowData;
        if (type === ROW_BLOCK) {
          data.cell.styles.fillColor = PDF_COLORS.blockBg;
          data.cell.styles.textColor = PDF_COLORS.blockFg;
          data.cell.styles.fontStyle = 'bold';
        } else if (type === ROW_TOTAL) {
          data.cell.styles.fillColor = PDF_COLORS.totalBg;
          data.cell.styles.textColor = PDF_COLORS.totalFg;
          data.cell.styles.fontStyle = 'bold';
        } else {
          if (isAltLine) data.cell.styles.fillColor = PDF_COLORS.lineAltBg;
          data.cell.styles.textColor = PDF_COLORS.lineFg;
        }
      }
    }
  });

  doc.save(`${filename}.pdf`);
}
